<template>
  <div class="dropdown-wrapper" :class="{ 'dropdown-wrapper--error': errors.length }">
    <label v-if="label" class="dropdown-wrapper__label flex items-center gap-1" :for="name">
      {{ label }} <span v-if="errorMessage?.trim()">- {{ errorMessage }}</span>
      <UITooltip v-if="tooltip" content-width="230px">
        <UIIcon size="xs" name="info-circle"></UIIcon>
        <template #content>
          {{ tooltip }}
        </template>
      </UITooltip>
    </label>
    <div
      ref="dropdown"
      class="dropdown"
      :class="[
        { 'dropdown--focused': isMenuVisible },
        { 'dropdown--selected-value': inputValue },
        { 'dropdown--disabled': disabled },
        { 'dropdown--loading': loading },
        { 'dropdown--compact': compact },
      ]"
    >
      <UIIcon v-if="leftIcon" class="dropdown__left-icon" :name="leftIcon" />
      <UIDropdownValue
        :multiple="multiple"
        :value="inputValue"
        :label-key="labelKey"
        :items="items"
        :persistent-placeholder="persistentPlaceholder"
        :value-key="valueKey"
        :disabled="disabled"
        @focus="setFocus"
        @clear="handleClear"
      />
      <input
        :id="`${name}-dropdown`"
        ref="input"
        v-model="search"
        autocomplete="off"
        type="text"
        class="dropdown__input"
        :disabled="disabled"
        :class="{ 'dropdown__input--persistent-placeholder': getIsPersistentPlaceholderActive() }"
        :placeholder="getPlaceholder()"
        @focus="handleFocus"
        @input="handleInput"
        @keydown.backspace="handleBackspace"
      />
      <UIBaseTransition>
        <div v-if="loading" class="dropdown__loader">
          <UIProgressSpinner width="20" height="20" />
        </div>
        <div v-else-if="removable" class="dropdown__remove" @click.stop="handleRemoveClick">
          <UIIcon name="big-close-circle-filled" />
        </div>
        <div
          v-else
          class="dropdown__chevron"
          :class="{ 'dropdown__chevron--open': isMenuVisible }"
          @click="handleChevronClick"
        >
          <UIIcon name="chevron-big-filled-down" />
        </div>
      </UIBaseTransition>
    </div>
    <ClientOnly>
      <Teleport v-if="isMounted" :to="`#${name}-popup-menu`" :disabled="isMobile && isMounted ? !props.multiple : true">
        <UIBaseTransition>
          <UIMenu
            v-if="isMenuVisible || isMobilePopupVisible"
            ref="menu"
            :multiple
            :model-value="isMobileMultiple ? shallowInputValue : inputValue"
            :search-value="search"
            :menu-visible="isMenuVisible"
            :style="isMobileMultiple ? undefined : floatingStyles"
            :value-key
            :menu-width
            :menu-height="isMobileMultiple ? String(mobilePopupBodyHeight) : menuHeight"
            :loading
            :searchable="searchable || isMobileMultiple"
            :items="filteredItems"
            :search-placeholder
            :search-key
            :item-size
            :virtual-scroll
          >
            <template #default="{ item, active }">
              <UIMenuOption
                :multiple
                :active
                :item
                :avatar
                :label-key
                @select="isMobileMultiple ? handleMobileSelect($event) : handleSelect($event)"
              >
                <slot :active :item />
              </UIMenuOption>
            </template>
            <template v-if="loading" #custom> Loading... </template>
          </UIMenu>
        </UIBaseTransition>
      </Teleport>
    </ClientOnly>
    <template v-if="isMobile">
      <UIPopup
        v-show="isMobilePopupVisible"
        class="dropdown__mobile-popup"
        disable-body-lock
        closeable
        @close="handleMobileClose"
        @apply="handleMobileApply"
      >
        <template #header>{{ label }}</template>
        <template #body>
          <div :id="`${name}-popup-menu`" ref="mobilePopupBody" class="dropdown__mobile-popup-body"></div>
        </template>
      </UIPopup>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useFloating, autoUpdate, flip } from '@floating-ui/vue'
import { useField } from 'vee-validate'
import isArray from 'lodash/isArray'
import type { MenuOptionProps } from '../menu/Option.vue'

import type { UIMenu } from '#build/components'
import type { MenuProps } from '~/types/ui'

export type DropdownProps = {
  items?: any[]
  modelValue?: any
  name: string
  multiple?: boolean
  disabled?: boolean
  syncVModel?: boolean
  persistentPlaceholder?: boolean
  valueKey?: string
  labelKey?: string
  label?: string
  placeholder: string
  loading?: boolean
  leftIcon?: string
  clearable?: boolean
  compact?: boolean
  removable?: boolean
  tooltip?: string
} & Omit<MenuProps, 'menuVisible'>

const props = withDefaults(defineProps<DropdownProps>(), {
  items: () => [],
  modelValue: undefined,
  label: '',
  multiple: false,
  syncVModel: false,
  persistentPlaceholder: false,
  menuWidth: '100%',
  menuHeight: undefined,
  disabled: false,
  loading: false,
  leftIcon: '',
  clearable: true,
  labelKey: 'name',
  valueKey: 'id',
  tooltip: '',
  fullWidth: true,
})

defineSlots<{
  default: (props: MenuOptionProps) => any
}>()

const emit = defineEmits<{
  (e: 'clear'): void
  (e: 'remove'): void
  (e: 'update:modelValue', value: any): void
}>()

const name = toRef(props, 'name')

const {
  value: inputValue,
  errorMessage,
  errors,
  handleChange,
} = useField<string | number | number[] | undefined>(name, undefined, {
  initialValue: props.modelValue,
  syncVModel: props.syncVModel,
})

const { isMobile } = useAppBreakpoints()

const isMobileMultiple = computed(() => isMobile && props.multiple)

const shallowInputValue = ref<number[]>([])

const handleMobileSelect = (value: any) => {
  const valueFormatted = value[props.valueKey]

  if (isArray(shallowInputValue.value)) {
    const isValuePresent = shallowInputValue.value.includes(valueFormatted)

    if (isValuePresent) {
      // Remove from existing
      shallowInputValue.value = shallowInputValue.value.filter((id: number) => id !== valueFormatted)
    } else {
      // Add to existing
      shallowInputValue.value = [...shallowInputValue.value, valueFormatted]
    }
  } else {
    // Create new one
    shallowInputValue.value = [valueFormatted]
  }

  if (menu.value) {
    if (menu.value.searchRef?.input?.input) {
      menu.value.searchRef.input.handleClear()
    }

    if (menu.value.list) {
      menu.value.list.scrollTop = 0
    }
  }
}

const { lock, unlock } = useBodyScrollLock()

const handleMobileClose = () => {
  isMobilePopupVisible.value = false

  unlock()

  shallowInputValue.value = []
}

const handleMobileApply = () => {
  handleChange(shallowInputValue.value)

  shallowInputValue.value = []

  isMobilePopupVisible.value = false

  unlock()
}

const menu = ref<InstanceType<typeof UIMenu> | null>(null)

const handleSelect = (value: any) => {
  const valueFormatted = value[props.valueKey]

  if (props.multiple) {
    if (isArray(inputValue.value)) {
      const isValuePresent = inputValue.value.includes(valueFormatted)

      if (isValuePresent) {
        // Remove from existing
        handleChange(inputValue.value.filter((id: number) => id !== valueFormatted))
      } else {
        // Add to existing
        handleChange([...inputValue.value, valueFormatted])
      }
    } else {
      // Create new one
      handleChange([valueFormatted])
    }

    search.value = ''

    if (menu.value && menu.value.list) {
      menu.value.list.scrollTop = 0
    }

    setFocus()

    return
  } else {
    // eslint-disable-next-line eqeqeq
    const isValueEqual = valueFormatted == inputValue.value

    handleChange(isValueEqual && props.clearable ? undefined : valueFormatted)
  }

  emit('update:modelValue', valueFormatted)

  isMenuVisible.value = false
  search.value = ''
}

const getPlaceholder = () => {
  if (props.multiple && isArray(inputValue.value) && props.persistentPlaceholder) {
    return props.placeholder
  }

  if (props.loading) return props.placeholder

  return inputValue.value ? '' : props.placeholder
}

const getIsPersistentPlaceholderActive = () => {
  if (props.multiple && isArray(inputValue.value)) {
    return Boolean(inputValue.value.length)
  }

  return false
}

const handleClear = () => {
  handleChange(undefined)

  isMenuVisible.value = false

  emit('clear')
}

const handleBackspace = (event: KeyboardEvent) => {
  const value = (event.target as HTMLInputElement).value

  if (!value && props.clearable) {
    if (props.multiple) {
      handleChange(inputValue.value ? (inputValue.value as number[]).slice(0, -1) : undefined)
      return
    }

    handleChange(undefined)
  }
}

const input = ref<HTMLInputElement | null>(null)

const setFocus = () => {
  input.value?.focus()
}

const mobilePopupBody = ref<HTMLElement | null>(null)

const { height: mobilePopupBodyHeight } = useElementSize(mobilePopupBody)

const isMobilePopupVisible = ref(false)

const handleFocus = () => {
  if (isMobile && props.multiple) {
    shallowInputValue.value = isArray(inputValue.value) ? (inputValue.value as number[]) : []
    isMobilePopupVisible.value = true

    lock()

    // Popup is visible, set focus on search input
    nextTick(() => {
      if (menu.value) {
        if (menu.value.searchRef?.input?.input) {
          menu.value.searchRef.input.input.focus()
        }
      }
    })
  }

  if (!isMobile) {
    isMenuVisible.value = true
  }

  setFocus()
}

const handleInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value

  if (isMobile) {
    if (value.length >= 2) {
      isMenuVisible.value = true
    } else {
      // Just for animation
      setTimeout(() => {
        isMenuVisible.value = false
      }, 50)
    }
  }
}

const handleChevronClick = () => {
  isMenuVisible.value = !isMenuVisible.value

  if (isMenuVisible.value) {
    setFocus()
  }
}

const handleRemoveClick = () => {
  isMenuVisible.value = false

  emit('remove')
}

const search = ref('')

const { filter } = useFilterItems()

const filteredItems = computed(() => {
  const filteredItems = search.value ? filter(props.items, search.value, props.searchKey) : props.items
  const array = isMobileMultiple.value ? shallowInputValue.value : inputValue.value

  if (props.multiple && isArray(array)) {
    // Created this one just for TypeScript
    const values = array

    // Do not mutate the original array
    return [...filteredItems].sort((a, b) => values.indexOf(b[props.valueKey]) - values.indexOf(a[props.valueKey]))
  }
  return filteredItems
})

const dropdown = ref()

const isMenuVisible = ref(false)

onClickOutside(
  menu,
  () => {
    isMenuVisible.value = false
    search.value = ''
  },
  { ignore: [dropdown] },
)

const isMounted = ref(false)

onMounted(() => {
  // To prevent teleport error warning
  setTimeout(() => {
    isMounted.value = true
  }, 500)
})

const { floatingStyles } = useFloating(dropdown, menu, {
  placement: isMobile ? 'top-start' : 'bottom-start',
  whileElementsMounted: autoUpdate,
  strategy: 'absolute',
  middleware: [
    flip({
      fallbackPlacements: isMobile ? ['top-start'] : ['bottom-start', 'top-start', 'bottom-end', 'top-end'],
    }),
  ],
  open: isMenuVisible,
})
</script>

<style scoped lang="scss">
@import '@/styles/variables.scss';

.dropdown {
  padding: 8px 16px;
  border-radius: $base-border-radius;
  border: $base-border-width solid $black-20;
  height: 48px;
  display: flex;
  align-items: center;
  transition: $base-transition all;
  background: white;
  color: $black-80;

  &__chevron,
  &__remove {
    display: flex;
    color: $black-60;
    margin-left: 4px;
    flex-shrink: 0;
    transition: $base-transition all;
    cursor: pointer;
    user-select: none;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__left-icon {
    display: flex;
    color: $black-60;
    width: 24px;
    height: 24px;
    margin-right: 8px;
  }

  &__loader {
    width: 20px;
    height: 20px;
    color: $black-30;
  }

  &-wrapper {
    font-size: 14px;
    line-height: 140%;
    color: $black-80;
    position: relative;

    &__label {
      margin-bottom: 2px;
      margin-left: 16px;
      font-weight: 500;
    }

    &--error {
      .dropdown {
        border: $base-border-width solid $error-100;
      }

      .dropdown-wrapper__label {
        color: $error-100;
      }

      @media screen and (max-width: 1023px) {
        .dropdown__input {
          &:placeholder-shown,
          &::placeholder {
            color: $error-100;
          }
        }
      }
    }
  }

  &:hover {
    border: $base-border-width solid $black-60;
  }

  &--compact {
    height: 40px;
    padding: 8px 12px;
  }

  &--disabled {
    background: $black-05;
    border: $base-border-width solid $black-20 !important;
    color: $black-60;

    .dropdown__chevron {
      color: $black-60;
      pointer-events: none;
    }
  }

  &--focused {
    border: $base-border-width solid $black-100 !important;

    .dropdown__chevron {
      color: $black-100;
    }
  }

  &__input {
    width: 100%;
    border: 0;
    outline: none;
    font-size: 14px;
    transition:
      $base-transition background,
      $base-transition color;

    @media screen and (max-width: 1023px) {
      font-size: 16px;
    }

    &:disabled {
      background: $black-05;
      color: $black-60;

      &::placeholder {
        color: $black-60;
      }
    }

    &::placeholder {
      color: $black-40;
    }

    &--persistent-placeholder {
      &::placeholder {
        color: $black-100;
      }
    }

    &:placeholder-shown {
      text-overflow: ellipsis;
    }
  }

  &__mobile-popup {
    &-body {
      height: 100%;
    }

    :deep() {
      .popup {
        &__content {
          height: calc(100% - 32px);

          &-body {
            height: 100%;
          }

          .menu {
            box-shadow: none;
            border-radius: 0;
            border: none;
            height: 100%;
            width: 100%;
            padding: 0;

            &__list {
              margin-top: 4px;
            }
          }
        }
      }
    }
  }
}
</style>
